import React from "react";
import { styled, Box } from "@mui/material";

// RangeChip Size 타입 정의
export type RangeChipSize = "XS" | "s" | "m";

/**
 * 심각도/범위 시각화 칩 props — `activeDots` 개수만큼 색상 바가 채워진다.
 * 주로 이슈 심각도(grade) 표시에 사용. 크기별 최대 dot 수: XS=4, s=5, m=6.
 */
export interface RangeChipProps {
  /**
   * 텍스트 표시 여부
   */
  text?: boolean;
  /**
   * RangeChip 크기
   */
  size?: RangeChipSize;
  /**
   * 컬러 코드 (hex, rgb, rgba 등)
   */
  colorCode?: string;
  /**
   * 표시할 텍스트 (text가 true일 때)
   */
  label?: string;
  /**
   * 현재 활성화된 dot 개수 (0부터 size에 따른 최대값까지)
   */
  activeDots?: number;
  /**
   * 추가 CSS 클래스명
   */
  className?: string;
  /** 테스트 앵커. 루트로 전파된다(`...props`). 값 규약은 `src/test/testIds.ts` 참조. */
  "data-testid"?: string;
}

// Size별 dot 개수 정의
const getDotCount = (size: RangeChipSize): number => {
  switch (size) {
    case "XS":
      return 4;
    case "s":
      return 5;
    case "m":
      return 6;
    default:
      return 5;
  }
};

// Size별 스타일 정의
const getSizeStyles = (size: RangeChipSize) => {
  switch (size) {
    case "XS":
      return {
        fontSize: "10px",
        barWidth: "3px",
        barHeight: "8px",
        gap: "2px",
      };
    case "s":
      return {
        fontSize: "11px",
        barWidth: "4px",
        barHeight: "10px",
        gap: "3px",
      };
    case "m":
      return {
        fontSize: "12px",
        barWidth: "5px",
        barHeight: "12px",
        gap: "4px",
      };
    default:
      return {
        fontSize: "11px",
        barWidth: "4px",
        barHeight: "10px",
        gap: "3px",
      };
  }
};

// Styled Container
const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "size" && prop !== "colorCode",
})<{ size: RangeChipSize; colorCode: string }>(({ size, colorCode }) => {
  const sizeStyles = getSizeStyles(size);

  return {
    display: "inline-flex",
    alignItems: "center",
    gap: sizeStyles.gap,
    borderRadius: "4px",
    backgroundColor: "transparent",
    fontFamily: "Pretendard",
    fontWeight: 500,
    fontSize: sizeStyles.fontSize,
    lineHeight: 1,
    color: colorCode,
    minHeight: "20px",
  };
});

// Styled Text
const StyledText = styled("span")<{ size: RangeChipSize }>(({ size }) => {
  const sizeStyles = getSizeStyles(size);

  return {
    fontSize: sizeStyles.fontSize,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
  };
});

// Styled Bar
const StyledBar = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "size" && prop !== "colorCode" && prop !== "isActive",
})<{
  size: RangeChipSize;
  colorCode: string;
  isActive: boolean;
}>(({ size, colorCode, isActive }) => {
  const sizeStyles = getSizeStyles(size);

  return {
    width: sizeStyles.barWidth,
    height: sizeStyles.barHeight,
    borderRadius: "2px",
    backgroundColor: isActive ? colorCode : "rgba(0, 0, 0, 0.12)",
    border: `1px solid ${isActive ? colorCode : "none"}`,
    flexShrink: 0,
  };
});

// Styled Bars Container
const StyledBarsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "size",
})<{ size: RangeChipSize }>(({ size }) => {
  const sizeStyles = getSizeStyles(size);

  return {
    display: "flex",
    alignItems: "center",
    gap: sizeStyles.gap,
  };
});

/**
 * RangeChip 컴포넌트
 *
 * 텍스트와 막대(bar)로 구성된 범위 표시 컴포넌트입니다.
 * Figma 디자인 시스템을 기반으로 구현되었습니다.
 */
export const RangeChip: React.FC<RangeChipProps> = ({
  text = false,
  size = "s",
  colorCode = "#FF4444", // 기본값: 심각도 색상
  label = "심각도",
  activeDots = 0,
  className,
  ...props
}) => {
  const barCount = getDotCount(size);
  const sizeStyles = getSizeStyles(size);

  // activeDots가 유효한 범위 내에 있도록 제한
  const validActiveDots = Math.max(0, Math.min(activeDots, barCount));

  // bars 배열 생성
  const bars = Array.from({ length: barCount }, (_, index) => (
    <StyledBar
      key={index}
      size={size}
      colorCode={colorCode}
      isActive={index < validActiveDots}
    />
  ));

  return (
    <StyledContainer
      size={size}
      colorCode={colorCode}
      className={className}
      {...props}
    >
      {text && <StyledText size={size}>{label}</StyledText>}
      <StyledBarsContainer size={size}>{bars}</StyledBarsContainer>
    </StyledContainer>
  );
};

export default RangeChip;
